import { useEffect, useRef, useState } from "react";
import logo from "@/assets/mission-ready-logo.webp";

type Position = "center" | "top-right" | "bottom-left" | "tile";

interface AppBackgroundBrandProps {
  /** Logo opacity, 0-1. Default 0.05 (5%). Recommended 0.03–0.08. */
  opacity?: number;
  /** Blur amount in px applied to the watermark. */
  blur?: number;
  /** Enable subtle parallax (desktop only, respects reduced-motion). */
  animate?: boolean;
  /** Watermark placement. */
  position?: Position;
  /** Render a soft vignette over the page. */
  vignette?: boolean;
  /** Extra className on the wrapper. */
  className?: string;
}

/**
 * Ambient MissionReady brand watermark for the authenticated app.
 * - GPU-friendly (transform/opacity only, will-change hints)
 * - Disables animation on touch / reduced-motion / coarse pointer
 * - Sits behind all content (pointer-events: none, fixed, z-[-1] alt: 0 with -z behind via parent)
 */
export function AppBackgroundBrand({
  opacity = 0.05,
  blur = 1,
  animate = true,
  position = "center",
  vignette = true,
  className = "",
}: AppBackgroundBrandProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const coarse = window.matchMedia("(pointer: coarse)").matches;
    const allow = animate && !reduced && !coarse;
    setEnabled(allow);
    if (!allow) return;
    const el = ref.current;
    if (!el) return;
    let raf = 0;
    const onMove = (e: MouseEvent) => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const x = (e.clientX / window.innerWidth - 0.5) * 14;
        const y = (e.clientY / window.innerHeight - 0.5) * 14;
        el.style.transform = `translate3d(${x}px, ${y}px, 0)`;
      });
    };
    window.addEventListener("mousemove", onMove, { passive: true });
    return () => {
      window.removeEventListener("mousemove", onMove);
      cancelAnimationFrame(raf);
    };
  }, [animate]);

  const placement: Record<Position, string> = {
    center: "items-center justify-center",
    "top-right": "items-start justify-end p-12",
    "bottom-left": "items-end justify-start p-12",
    tile: "items-center justify-center",
  };

  return (
    <div
      aria-hidden
      className={`pointer-events-none fixed inset-0 -z-10 overflow-hidden ${className}`}
    >
      {/* Base radial wash — softens the watermark into the chrome */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse at 50% 30%, color-mix(in oklab, var(--primary) 6%, transparent), transparent 60%)",
        }}
      />

      {/* Watermark logo */}
      <div className={`absolute inset-0 flex ${placement[position]}`}>
        <div
          ref={ref}
          className="relative will-change-transform"
          style={{
            transition: enabled ? "transform 400ms cubic-bezier(.2,.7,.2,1)" : undefined,
          }}
        >
          <img
            src={logo}
            alt=""
            draggable={false}
            className="select-none"
            style={{
              width: "min(78vmin, 980px)",
              height: "auto",
              opacity,
              filter: `blur(${blur}px) saturate(0.85) contrast(1.05)`,
              mixBlendMode: "screen",
            }}
          />
          {/* Crimson glow halo behind the mark */}
          <div
            className="absolute inset-0 -z-10"
            style={{
              background:
                "radial-gradient(ellipse at center, color-mix(in oklab, var(--primary) 22%, transparent), transparent 65%)",
              filter: "blur(60px)",
              opacity: 0.55,
            }}
          />
        </div>
      </div>

      {/* Vignette */}
      {vignette && (
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(ellipse at center, transparent 55%, color-mix(in oklab, var(--background) 85%, transparent) 100%)",
          }}
        />
      )}

      {/* Tactical grid overlay */}
      <div
        className="absolute inset-0 opacity-[0.035]"
        style={{
          backgroundImage:
            "linear-gradient(to right, currentColor 1px, transparent 1px), linear-gradient(to bottom, currentColor 1px, transparent 1px)",
          backgroundSize: "44px 44px",
          color: "var(--foreground)",
          maskImage:
            "radial-gradient(ellipse at center, black 40%, transparent 80%)",
          WebkitMaskImage:
            "radial-gradient(ellipse at center, black 40%, transparent 80%)",
        }}
      />
    </div>
  );
}

export default AppBackgroundBrand;
